import { ComponentProps, ReactNode, createElement, useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { ID, usePlatformFeatureFlag } from "@datassential/platform-ui-lib";
import { useFoodSocial } from "@apiHooks/useFoodSocial";
import { useFoodSocialPosts } from "@apiHooks/useFoodSocialAssociationPosts";
import CopyButton from "@components/new/Table/CopyButton";
import { IFoodSocialCustomAssociation } from "@models/FoodSocialCustomAssociation";
import { FoodSocialMetricEnum } from "@models/FoodSocial";
import { SocialPosts } from "@models/SocialPosts";
import {
    FOOD_SOCIAL_COPY_DATA_CONFIG,
    FOOD_SOCIAL_CUSTOM_ASSOCIATION_FLAG,
    FOOD_SOCIAL_MINIMAL_FILTERS_FLAG,
    FoodSocialSortFilterEnum,
} from "./useFoodSocialSection.constants";

export type FoodSocialFilterKind = 'month' | 'association' | 'custom';

export interface IFoodSocialActiveFilter {
    kind: FoodSocialFilterKind;
    label: string;
}

/**
 * State of the Food Profile Social section, shared by the legacy and the refreshed page.
 * Only one posts filter is active at a time: a month bar, a top association, or a custom association.
 */
export default function useFoodSocialSection({
    wordId,
    setHasData,
}: {
    wordId: ID;
    setHasData: (hasData: boolean) => void;
}) {
    const isMinimalFiltersFeatureEnabled = usePlatformFeatureFlag(FOOD_SOCIAL_MINIMAL_FILTERS_FLAG);
    const isCustomAssociationEnabled = usePlatformFeatureFlag(FOOD_SOCIAL_CUSTOM_ASSOCIATION_FLAG);
    const [selectedMetric, setSelectedMetric] = useState<FoodSocialMetricEnum>(FoodSocialMetricEnum.Mentions);
    const [
        selectedSortFilterMetric,
        setSelectedSortFilterMetric,
    ] = useState<FoodSocialSortFilterEnum>(FoodSocialSortFilterEnum.Engagement);
    const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
    const [selectedYear, setSelectedYear] = useState<string | null>(null);
    const [selectedAssociation, setSelectedAssociation] = useState<string>('');
    const [
        selectedCustomAssociation,
        setSelectedCustomAssociation,
    ] = useState<IFoodSocialCustomAssociation | null>(null);
    // Bumped when the custom filter is removed from outside the search box, so the box can be remounted empty.
    const [customAssociationResetKey, setCustomAssociationResetKey] = useState(0);

    const onMentionBarClick = useCallback(
        (month: number | null, year: string | null) => {
            setSelectedMonth(month);
            setSelectedYear(year);
            setSelectedAssociation('');
            setSelectedCustomAssociation(null);
        },
        []
    );

    const onAssociationBarClick = useCallback(
        (association: string) => {
            setSelectedAssociation(association);
            setSelectedMonth(null);
            setSelectedYear(null);
            setSelectedCustomAssociation(null);
        },
        []
    );

    const onCustomAssociationClick = useCallback(
        (result: IFoodSocialCustomAssociation | null) => {
            setSelectedCustomAssociation(result);
            setSelectedAssociation('');
            setSelectedMonth(null);
            setSelectedYear(null);
        },
        []
    );

    /** Legacy "clear" button: resets the sort and the association filters, keeps a selected month. */
    const clearAssociations = useCallback(
        () => {
            setSelectedSortFilterMetric(FoodSocialSortFilterEnum.Engagement);
            setSelectedAssociation('');
            setSelectedCustomAssociation(null);
        },
        []
    );

    // top association names arrive upper-cased from the API; match that for the user's custom query
    const associationLabel = selectedAssociation || selectedCustomAssociation?.query.trim().toUpperCase() || '';

    const activeFilter = useMemo(
        (): IFoodSocialActiveFilter | null => {
            if (selectedCustomAssociation) {
                return { kind: 'custom', label: selectedCustomAssociation.query.trim() };
            }
            if (selectedAssociation) {
                return { kind: 'association', label: selectedAssociation };
            }
            if (selectedMonth !== null && selectedYear !== null) {
                return { kind: 'month', label: dayjs(`${selectedYear}-${selectedMonth}-01`).format('MMMM YYYY') };
            }
            return null;
        },
        [selectedCustomAssociation, selectedAssociation, selectedMonth, selectedYear]
    );

    /** Back to the default posts (past 90 days). Removing a custom filter also empties the search box. */
    const clearFilter = useCallback(
        () => {
            if (selectedCustomAssociation) {
                setCustomAssociationResetKey((key) => key + 1);
            }
            onMentionBarClick(null, null);
        },
        [selectedCustomAssociation, onMentionBarClick]
    );

    const isCustomPostsApplied = selectedAssociation !== '' || selectedMonth !== null || selectedYear !== null;

    const {
        isLoading,
        data: {
            hasData,
            permissionLevel,
            data,
        },
    } = useFoodSocial(wordId);

    const {
        isLoading: isAssociationPostsLoading,
        data: associationPostsData,
    } = useFoodSocialPosts(
        wordId,
        selectedAssociation,
        selectedYear,
        selectedMonth,
    );

    useEffect(() => {
        setHasData(!isLoading && hasData);
    }, [setHasData, isLoading, hasData]);

    const postsSource = useMemo(
        () => {
            if (selectedCustomAssociation) {
                return selectedCustomAssociation.posts;
            }

            return isCustomPostsApplied
                ? associationPostsData
                : data.posts;
        },
        [data, associationPostsData, isCustomPostsApplied, selectedCustomAssociation]
    );

    const postsData = useMemo(
        () => {
            if (selectedSortFilterMetric === FoodSocialSortFilterEnum.Negative) {
                return postsSource.negative;
            } else if (selectedSortFilterMetric === FoodSocialSortFilterEnum.Positive) {
                return postsSource.positive;
            } else if (selectedSortFilterMetric === FoodSocialSortFilterEnum.Newest) {
                return postsSource.newest;
            }

            return postsSource.regular;
        },
        [postsSource, selectedSortFilterMetric]
    );

    const postsDateRangeString = useMemo(
        (): ReactNode => {
            if (selectedCustomAssociation?.posts.dateRangeStr) {
                return selectedCustomAssociation.posts.dateRangeStr;
            }

            if (selectedMonth !== null && selectedYear !== null) {
                const date = dayjs(`${selectedYear}-${selectedMonth}-02`);
                return createElement('b', null, SocialPosts.getDateRange(date, date));
            }

            return data.posts.dateRangeStr;
        },
        [data.posts.dateRangeStr, selectedMonth, selectedYear, selectedCustomAssociation]
    );

    const copyPostsData = useMemo(
        (): ComponentProps<typeof CopyButton>['data'] => [
            FOOD_SOCIAL_COPY_DATA_CONFIG.map(({ title }) => title),
            ...postsData.map(
                (post) => FOOD_SOCIAL_COPY_DATA_CONFIG.map(
                    ({ key, valueFormatter }) => {
                        const value = post[key];
                        return valueFormatter ? valueFormatter(`${value}`) : `${value}`;
                    }
                )
            ),
        ],
        [postsData]
    );

    const sortedByString = selectedSortFilterMetric === FoodSocialSortFilterEnum.Newest ? 'by date' : 'by engagement';

    const postsTabs = useMemo(
        () => {
            if (isMinimalFiltersFeatureEnabled) {
                return [
                    { id: FoodSocialSortFilterEnum.Engagement, title: 'Engagement' },
                    { id: FoodSocialSortFilterEnum.Newest, title: 'Newest' },
                ];
            }
            return [
                { id: FoodSocialSortFilterEnum.Newest, title: 'Newest' },
                { id: FoodSocialSortFilterEnum.Positive, title: 'Positive' },
                { id: FoodSocialSortFilterEnum.Negative, title: 'Negative' },
            ];
        },
        [isMinimalFiltersFeatureEnabled],
    );

    // prefix custom selections so a custom "soda" never shares a key (and carousel state) with a top "SODA"
    const postsSectionKey = `${selectedSortFilterMetric}-${selectedCustomAssociation ? 'custom:' : ''}${associationLabel}-${selectedMonth}-${selectedYear}`;

    return {
        isLoading,
        hasData,
        permissionLevel,
        data,
        isMinimalFiltersFeatureEnabled,
        isCustomAssociationEnabled,
        selectedMetric,
        setSelectedMetric,
        selectedSortFilterMetric,
        setSelectedSortFilterMetric,
        selectedMonth,
        selectedYear,
        selectedAssociation,
        selectedCustomAssociation,
        onMentionBarClick,
        onAssociationBarClick,
        onCustomAssociationClick,
        clearAssociations,
        clearFilter,
        activeFilter,
        customAssociationResetKey,
        associationLabel,
        postsData,
        postsDateRangeString,
        sortedByString,
        copyPostsData,
        postsTabs,
        postsSectionKey,
        isPostsLoading: !selectedCustomAssociation && isAssociationPostsLoading,
    };
}
